<script lang="ts">
	import imgTeamOstravaDetailed from "@assets/images/about/fastest-team-ostrava-3.jpg?webp&w=312&imagetools";
	import { Background } from "@components/Background.js";
	import BookMeeting from "@components/BookMeeting.svelte";
	import Card from "@components/Card.svelte";
	import Section from "@components/Section.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import Media from "@components/common/Media.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import envelope from "@core/assets/icons/envelope.svg?raw";
	import headphones from "@core/assets/icons/headphones.svg?raw";
	import logoSolutionHorizontalDarkBackground from "@core/assets/images/fastest-logo-variants/fastest-zakladni-horizontal-logo+solution-pro-tmave-pozadi-rgb.svg";
	import { getCareerPath } from "@core/schema/paths/getCareerPath.js";
	import { getConnectWithFastestPath } from "@core/schema/paths/getConnectWithFastestPath.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { invoice, office } from "../data/fastestCompanyInfo.js";
	import { BookMeetingPerson, getBookMeetingPeople } from "../data/getBookMeetingPeople.js";
	import type { ContactProps } from "./ContactProps.js";

	const _: ContactProps = $props();
	const { tenant } = getGlobalTemplateProps();
</script>

<Layout>
	<Section
		paddingFollowNext
		background={Background.LightGradient}
		centered
		gridHeadline="container-6"
		gridDesc="container-6"
		hero
	>
		{#snippet headline()}
			<Formatted text="Pojďme to cg[[společně]] rozjet" />
		{/snippet}
		{#snippet desc()}
			<p>
				<span class="block font-medium text-fstonyx"
					><Formatted text="Kontaktujte nás ohledně poptávky služeb, podpory či kariérních příležitostí." /></span
				>
				<Formatted text="Nebo se jen tak zeptejte, rádi odpovíme." />
			</p>
		{/snippet}
		{#snippet cta()}
			<div class="flex gap-3 w-full flex-col items-center">
				<Link variant={ButtonVariant.PhloxOutlineLightBg} href={getConnectWithFastestPath(tenant)}>
					Poptávka služeb
				</Link>
				<Link variant={ButtonVariant.GreenOutlineLightBg} href={getCareerPath(tenant)}>Kariéra</Link>
			</div>
		{/snippet}
	</Section>

	<Section centered gridHeadline="container-6" gridChildren="container-12">
		{#snippet headline()}
			<Formatted text="Kde nás najdete?" />
		{/snippet}
		<div
			class={[
				"grid-cols-1 sm:grid-cols-2 grid justify-items-center sm:justify-items-stretch lg:grid-cols-3 gap-4 sm:gap-[1.03rem] mb-12 mx-auto",
			]}
		>
			<Card title="Office — Ostrava">
				{#snippet desc()}
					<Media
						source={{ src: imgTeamOstravaDetailed, type: "image/webp" }}
						class="mb-4 object-cover h-[10rem] rounded-lg"
						width={384}
						height={200}
						alt="{invoice.companyName} – {office.street}, {office.city}"
					/>
					<address class="not-italic font-medium">
						<span class="block text-fstonyx font-loos-wide">{invoice.companyName}</span>
						<span class="block">{office.street}</span>
						<span class="block">{office.city}</span>
						<span class="mt-4 block">3. patro <span class="text-fstonyx font-loos-wide">vyvíjíme</span></span>
						<span class="block">4. patro <span class="text-fstonyx font-loos-wide">schůzujeme</span></span>
					</address>
					<Link href="https://goo.gl/maps/JjbPWwYymSqHCEyK9" target="_blank" variant={ButtonVariant.PhloxOutlineLightBg}
						>Navigovat</Link
					>
				{/snippet}
			</Card>

			<Card title="Sídlo — Praha">
				{#snippet desc()}
					<div class={["mb-4 object-cover h-[10rem] w-full rounded-lg", Background.Onyx]}>
						<Media
							class={["h-full w-52 lg:w-96 m-auto px-8"]}
							source={{ src: logoSolutionHorizontalDarkBackground, type: "image/svg+xml" }}
							width={208}
							height={43.5}
							alt="Fastest Solution logo"
						/>
					</div>
					<address class="not-italic font-medium">
						<span class="block text-fstonyx font-loos-wide">{invoice.companyName}</span>
						<span class="block">{invoice.street}</span>
						<span class="block">{invoice.city}</span>
						<span class="mt-4 block">IČ <span class="text-fstonyx font-loos-wide">{invoice.ic}</span></span>
						<span class="block">DIČ <span class="text-fstonyx font-loos-wide">{invoice.dic}</span></span>
					</address>
				{/snippet}
			</Card>

			<Card title="Další informace" className="!max-w-lg">
				{#snippet desc()}
					<div class="flex-col flex gap-2 items-start mb-4">
						<Link
							href="mailto:{office.email}"
							class="mt-4 text-md xl:text-2md flex gap-2 justify-center text-fstgreen items-center !no-underline font-loos-wide"
						>
							<Icon icon={envelope} square class="h-8 text-fstonyx" />
							<span>{office.email}</span>
						</Link>

						<Link
							href="tel:{office.phoneCEO}"
							class="flex text-md xl:text-2md gap-2 text-fstgreen justify-center items-center !no-underline font-loos-wide"
						>
							<Icon icon={headphones} square class="h-8 text-fstonyx" />
							<span>{office.phoneCEO}</span>
						</Link>
						<Link
							href="tel:{office.phoneSupport}"
							class="flex text-md xl:text-2md gap-2 text-fstgreen justify-center items-center !no-underline font-loos-wide"
						>
							<Icon icon={headphones} square class="h-8 text-fstonyx" />
							<span>{office.phoneSupport}</span>
						</Link>
					</div>
					<p class="mb-0">
						<small class="font-loos-normal font-medium tracking-[0.5em] uppercase text-xs text-left">faktury</small>
						<span class="block font-loos-wide text-fstonyx">{invoice.email}</span>
					</p>
					<p class="mb-0">
						<small class="font-loos-normal font-medium tracking-[0.5em] uppercase text-xs text-left">Banka</small>
						<span class="block font-loos-wide text-fstonyx">{invoice.bankAccount}</span>
					</p>
					<p class="mb-0">
						<small class="font-loos-normal font-medium tracking-[0.5em] uppercase text-xs text-left">Datovka</small>
						<span class="block font-loos-wide text-fstonyx">{invoice.databox}</span>
					</p>
				{/snippet}
			</Card>
		</div>
	</Section>

	{@const contacts = getBookMeetingPeople(tenant)}
	<BookMeeting contacts={[contacts[BookMeetingPerson.Kucera]]} />
</Layout>
